import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getMyPayslipPdfApi, listMyPayslipsApi, MyPayslip } from "../api/meApi";
import EmployeeShell from "../components/EmployeeShell";
import { COLABORADOR_HOME } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";

import "./Colaborador.css";

const formatDate = (value: string) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" });
const formatMoney = (value: string) => Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Contracheques do próprio colaborador: só os meses fechados pelo DP (e as parcelas
 * do 13º). O PDF é a via do empregado do mesmo demonstrativo que o DP imprime.
 */
export default function MyPayslipsPage() {
  const goBack = useGoBack(COLABORADOR_HOME());
  const [items, setItems] = useState<MyPayslip[] | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    listMyPayslipsApi()
      .then(setItems)
      .catch((error: any) => {
        toast.error(error?.message || "Não foi possível carregar os seus contracheques.");
        setItems([]);
      });
  }, []);

  // Por ano do pagamento da competência, do mais recente (a lista já vem ordenada).
  const byYear = useMemo(() => {
    const groups: { year: number; items: MyPayslip[] }[] = [];
    for (const item of items ?? []) {
      const last = groups[groups.length - 1];
      if (last && last.year === item.year) last.items.push(item);
      else groups.push({ year: item.year, items: [item] });
    }
    return groups;
  }, [items]);

  const open = async (item: MyPayslip) => {
    // A aba é aberta ANTES do await: aberta depois, o bloqueador de pop-up a barra.
    const tab = window.open("", "_blank");
    setOpening(item.periodId);
    try {
      const url = URL.createObjectURL(await getMyPayslipPdfApi(item.periodId));
      if (tab) {
        tab.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `contracheque-${item.label.replace(/[^\w]+/g, "-")}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      tab?.close();
      toast.error(error?.message || "Não foi possível abrir o contracheque.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Meus contracheques
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost" onClick={goBack}>
            Voltar
          </button>
        </div>

        {items === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="colabHint">Nenhum contracheque disponível ainda. Ele aparece aqui quando o DP fecha o mês.</p>
        ) : (
          byYear.map((group) => (
            <div key={group.year}>
              <h3 className="colabPanel__title" style={{ fontSize: 16, margin: "16px 0 8px" }}>
                {group.year}
              </h3>
              <div className="colabList">
                {group.items.map((item) => (
                  <article key={item.periodId} className="colabListItem">
                    <div className="colabListItem__head">
                      <span>{item.label}</span>
                      <strong>{formatMoney(item.netAmount)}</strong>
                    </div>
                    <p className="colabListItem__meta">Pago em {formatDate(item.paymentDate)} · valor líquido</p>
                    <button
                      type="button"
                      className="colabBtn colabBtn--primary colabBtn--compact"
                      onClick={() => open(item)}
                      disabled={opening !== null}
                      style={{ marginTop: 8 }}
                    >
                      {opening === item.periodId ? "Abrindo..." : "Abrir PDF"}
                    </button>
                  </article>
                ))}
              </div>
            </div>
          ))
        )}

        <p className="colabFooterNote">
          Demonstrativo de pagamento emitido pelo sistema. Dúvidas sobre valores, procure o Departamento Pessoal.
        </p>
      </section>
    </EmployeeShell>
  );
}
